import React, { useMemo, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { Dropdown, Select, Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'

import styled from 'styled-components'
import { colours, sizes } from '@qflow/theme'

import {
  ColourButton,
  icons,
  Spacer,
  Text,
  UserAvatar
} from 'portal/lib/primitives'
import { ProjectUser } from 'portal/lib/interfaces'
import { ALERT_RULE_TITLE_MAPPER } from './constants'

export type RecipientType = {
  userId: string
}
type RuleWithRecipientsType = {
  alertKey: string
  recipients: RecipientType[]
}

export function AlertRecipientSelector({
  projectId,
  unsavedSelectedRecipients,
  setUnsavedSelectedRecipients,
  otherRulesWithRecipients
}: {
  projectId: string
  unsavedSelectedRecipients: RecipientType[]
  setUnsavedSelectedRecipients: React.Dispatch<
    React.SetStateAction<RecipientType[]>
  >
  otherRulesWithRecipients: RuleWithRecipientsType[] | null
}) {
  const [assignableUsers, setAssignableUsers] = useState<ProjectUser[]>([])

  const { Option } = Select

  const projectUsers = useSelector((state: any) => state.projectUsers)

  useEffect(() => {
    const assignableUserArray: ProjectUser[] = Object.values(projectUsers.users)

    // Filter out users that are already selected
    const filteredAssignableUserArray = assignableUserArray.filter(
      user =>
        !unsavedSelectedRecipients.some(
          recipient => recipient.userId === user.userId
        )
    )

    // Sort the assignable users by display name
    filteredAssignableUserArray.sort((a, b) => {
      const fa = a.displayName.toLowerCase()
      const fb = b.displayName.toLowerCase()

      if (fa < fb) {
        return -1
      } else if (fa > fb) {
        return 1
      }
      return 0
    }) as ProjectUser[]
    setAssignableUsers(filteredAssignableUserArray)
  }, [projectUsers, unsavedSelectedRecipients])

  const copyRecipientsMenuProps = useMemo(() => {
    return {
      items: otherRulesWithRecipients?.map(rule => ({
        key: rule.alertKey,
        label: ALERT_RULE_TITLE_MAPPER[rule.alertKey],
        onClick: () => {
          const dedupe = (arr: RecipientType[]): RecipientType[] => {
            const map = new Map<string, RecipientType>()
            arr.forEach(obj => map.set(obj.userId, obj))
            return Array.from(map.values())
          }

          const newSelectedRecipients = dedupe([
            ...unsavedSelectedRecipients,
            ...rule.recipients
          ])

          setUnsavedSelectedRecipients(newSelectedRecipients)
        }
      }))
    }
  }, [
    otherRulesWithRecipients,
    setUnsavedSelectedRecipients,
    unsavedSelectedRecipients
  ])

  const columns: ColumnsType<RecipientType> = [
    {
      title: 'User',
      dataIndex: 'userId',
      key: 'userId',
      width: '2rem',
      render: data => (
        <UserAvatar
          displayName={
            projectUsers.users[`${projectId}_${data}`]?.displayName || ''
          }
          displayTooltip={false}
        />
      )
    },
    {
      title: '',
      dataIndex: 'userId',
      key: 'recipient',
      render: (data: string, record) =>
        projectUsers.users[`${projectId}_${data}`]?.displayName
    },
    {
      title: 'Email',
      dataIndex: 'userId',
      key: 'email',
      render: (data: string) =>
        projectUsers.users[`${projectId}_${data}`]?.email
    },
    {
      title: '',
      dataIndex: 'userId',
      width: '2rem',
      render: (data: string) => (
        <Tooltip title="Remove user">
          <ColourButton.BasicBorderless
            aria-label={data}
            style={{ padding: '4px 9px', borderRadius: '50%' }}
            onClick={() => {
              const newSelectedRecipients = unsavedSelectedRecipients.filter(
                recipient => recipient.userId !== data
              )
              setUnsavedSelectedRecipients(newSelectedRecipients)
            }}
          >
            <icons.TrashCan colour={colours.ERROR} />
          </ColourButton.BasicBorderless>
        </Tooltip>
      )
    }
  ]

  const handleRecipientChange = value => {
    const newSelectedRecipients: RecipientType[] = [
      ...unsavedSelectedRecipients
    ]
    newSelectedRecipients.push({ userId: value })
    newSelectedRecipients.sort((a, b) => {
      const fa =
        projectUsers.users[
          `${projectId}_${a.userId}`
        ]?.displayName.toLowerCase()
      const fb =
        projectUsers.users[
          `${projectId}_${b.userId}`
        ]?.displayName.toLowerCase()

      if (fa < fb) {
        return -1
      } else if (fa > fb) {
        return 1
      }
      return 0
    })
    setUnsavedSelectedRecipients(newSelectedRecipients)
  }

  return (
    <>
      <h2>Alert Recipients</h2>
      <RecipientSelectWrapper>
        <RecipientSelect
          id="recipient-autocomplete"
          onChange={handleRecipientChange}
          placeholder="Search users..."
          autoClearSearchValue
          showSearch
          suffixIcon={<icons.MagnifyingGlass colour={'currentColor'} />}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          value={null}
          virtual={false}
        >
          {assignableUsers.length > 0 &&
            assignableUsers.map(user => (
              <Option
                key={user.userId}
                value={user.userId}
                label={`${user.displayName} ${user.email}`}
              >
                <div>
                  {user.displayName}
                  <br />
                  <Text size={sizes.X_SMALL} style={{ wordBreak: 'break-all' }}>
                    {user.email}
                  </Text>
                </div>
              </Option>
            ))}
        </RecipientSelect>
        {otherRulesWithRecipients && otherRulesWithRecipients.length > 0 && (
          <Dropdown
            className="copy-recipients-dropdown"
            menu={copyRecipientsMenuProps}
            trigger={['click']}
          >
            <ColourButton.Trigger style={{ margin: '0', minHeight: '41px' }}>
              Copy from alert
              <Spacer.Tiny />
              <icons.ChevronDown />
            </ColourButton.Trigger>
          </Dropdown>
        )}
      </RecipientSelectWrapper>
      <Table
        id="recipients-table"
        rowKey={row => row.userId}
        columns={columns}
        dataSource={unsavedSelectedRecipients}
        pagination={false}
        locale={{ emptyText: 'There are no selected recipients' }}
        size="middle"
        style={{ marginTop: '0.5rem' }}
      />
    </>
  )
}

const RecipientSelectWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 1rem;
`
const RecipientSelect = styled(Select)`
  margin-top: 1rem;
  width: 50%;

  &.ant-select-single .ant-select-selector {
    align-items: center;
    border-radius: 5px;
    height: auto;
    min-height: 45px;

    & .ant-select-selection-item {
      margin: 4px 0;
      line-height: 1.25;
    }

    & .ant-select-selection-item::after {
      content: none;
    }
  }
`
